/**
 * CR324: Preview Mode Toggle
 *
 * Minimal toggle to control which draft layers the preview assembler applies.
 * Placed near the preview workbench.
 */

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { PreviewMode } from "@/lib/deals/preview/assembleDealCardPreview";

interface PreviewModeToggleProps {
  value: PreviewMode;
  onChange: (mode: PreviewMode) => void;
  className?: string;
}

const MODES: { value: PreviewMode; label: string; tooltip: string }[] = [
  { value: "saved", label: "Saved", tooltip: "Show only persisted config (no drafts)" },
  { value: "templateDraft", label: "Template", tooltip: "Include Template Editor drafts" },
  { value: "componentDraft", label: "Components", tooltip: "Include Components Studio drafts" },
  { value: "both", label: "Both", tooltip: "Include all draft layers" },
];

export function PreviewModeToggle({ value, onChange, className }: PreviewModeToggleProps) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => {
        if (v) onChange(v as PreviewMode);
      }}
      className={className}
      size="sm"
    >
      {MODES.map((mode) => (
        <Tooltip key={mode.value}>
          <TooltipTrigger asChild>
            <ToggleGroupItem
              value={mode.value}
              className="text-[10px] px-2 h-6 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              {mode.label}
            </ToggleGroupItem>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {mode.tooltip}
          </TooltipContent>
        </Tooltip>
      ))}
    </ToggleGroup>
  );
}
